import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function PricingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Navigation />

      <div className="flex-1 pt-28 pb-20 px-6">
        <div className="max-w-4xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-20">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              GPU compute for quantitative research
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
              The same hardware used by major proprietary funds and hedge funds — instantly accessible
              to everyone. Competitive rates on state-of-the-art GPUs through our cloud provider partnerships,
              with AI-assisted tooling from PropAlgos.
            </p>
            <div className="flex items-center justify-center gap-6 mb-10">
              <div className="bg-white rounded-lg px-4 py-2">
                <img src="/icons/icons8-aws-96.svg" alt="AWS" className="h-7" />
              </div>
              <div className="bg-white rounded-lg px-4 py-2">
                <img src="/icons/icons8-google-cloud-96.svg" alt="Google Cloud" className="h-7" />
              </div>
            </div>
          </div>

          {/* Value Props */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-24 max-w-3xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <h3 className="text-lg font-medium mb-2 text-white">Built for Quant Research</h3>
              <p className="text-gray-400 text-sm">
                GPU-accelerated backtesting, ML training, and strategy optimization. AI guidance
                helps you iterate faster.
              </p>
            </div>

            <div className="flex flex-col items-center text-center">
              <h3 className="text-lg font-medium mb-2 text-white">Multi-Cloud, Best Price</h3>
              <p className="text-gray-400 text-sm">
                Compare GPUs across AWS, Google Cloud, and more. Pick the best cost for your
                workload — or let the platform recommend.
              </p>
            </div>

            <div className="flex flex-col items-center text-center">
              <h3 className="text-lg font-medium mb-2 text-white">Transparent, Per-Minute Billing</h3>
              <p className="text-gray-400 text-sm">
                Prepaid balance with real-time cost tracking. Stop an instance, stop paying.
                No minimums, no commitments.
              </p>
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-24">
            <h2 className="text-3xl font-bold text-white text-center mb-14">How it works</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-3xl mx-auto">
              <div className="text-center">
                <div className="w-12 h-12 border border-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-lg font-semibold text-white">1</span>
                </div>
                <h3 className="text-base font-medium text-white mb-2">Choose a GPU</h3>
                <p className="text-gray-400 text-sm">
                  Pick the GPU that fits your workload from live inventory across providers.
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 border border-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-lg font-semibold text-white">2</span>
                </div>
                <h3 className="text-base font-medium text-white mb-2">Launch a notebook</h3>
                <p className="text-gray-400 text-sm">
                  Start with pre-loaded data and AI tooling — ready to run in under a minute.
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 border border-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-lg font-semibold text-white">3</span>
                </div>
                <h3 className="text-base font-medium text-white mb-2">Pay per minute</h3>
                <p className="text-gray-400 text-sm">
                  Real-time cost visible in your dashboard. Stop the instance, stop the bill.
                </p>
              </div>
            </div>
          </div>

          {/* What you pay for */}
          <div className="mb-24">
            <h2 className="text-3xl font-bold text-white text-center mb-14">What you pay for</h2>

            <div className="max-w-3xl mx-auto space-y-4">
              <div className="bg-gray-900 rounded-lg p-6 border border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="sm:pr-8">
                  <h3 className="text-base font-semibold text-white mb-2">
                    Research compute (GPU sweeps)
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Provider compute at passthrough cost, plus a flat platform fee per
                    completed sweep. Every run returns an itemized receipt.{' '}
                    <button
                      onClick={() => navigate('/docs')}
                      className="text-white underline underline-offset-2 hover:text-gray-300 transition-colors"
                    >
                      See the docs
                    </button>
                  </p>
                </div>
                <div className="sm:text-right flex-shrink-0">
                  <div className="text-white font-medium whitespace-nowrap">
                    Compute passthrough
                  </div>
                  <div className="text-gray-400 text-sm whitespace-nowrap">
                    + $0.05 per sweep
                  </div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-6 border border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="sm:pr-8">
                  <h3 className="text-base font-semibold text-white mb-2">Signal feeds</h3>
                  <p className="text-gray-400 text-sm">
                    Scheduled screens and HMAC-signed emissions delivered to your execution
                    endpoint, with a full delivery audit trail.
                  </p>
                </div>
                <div className="sm:text-right flex-shrink-0">
                  <div className="text-white font-medium whitespace-nowrap">Pilot pricing</div>
                  <div className="text-gray-400 text-sm whitespace-nowrap">Contact us</div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-24">
            <h2 className="text-3xl font-bold text-white text-center mb-10">FAQ</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                <h3 className="text-base font-semibold text-white mb-3">Which cloud providers are available?</h3>
                <p className="text-gray-400 text-sm">
                  AWS and Google Cloud today. More providers coming.
                </p>
              </div>

              <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
                <h3 className="text-base font-semibold text-white mb-3">Any minimum commitments?</h3>
                <p className="text-gray-400 text-sm">
                  No minimums, no commitments. Pay only for compute time used. Start and stop freely.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <button
              onClick={() => navigate('/login')}
              className="px-8 py-4 bg-white hover:bg-gray-100 text-black font-medium rounded-lg transition-all duration-200"
            >
              Get Started
            </button>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}
